import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, throwError } from 'rxjs';
import {
  Acknowledgement,
  Acknowledgements,
  AcknowledgementType,
  AcknowledgeResponse,
  CompanyAcknowledgementResponse,
} from '../../models/acknowledgements.model';
import { DocumentWithAcknowledgement } from '../../models/document.model';
import { CompanyAcknowledgementsApiService } from '../company-acknowledgements-api/company-acknowledgements-api.service';
import { DocumentService } from '../document/document.service';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class AcknowledgementsService extends SmartrService {
  private readonly documentService = inject(DocumentService);
  private readonly companyAcksApi = inject(CompanyAcknowledgementsApiService);

  /**
   * Gets acknowledgements for a specific client from the Documents API docs needing ack
   * and the Company Acknowledgements API. Any source that errors falls back to empty —
   * sign-in does not fail when one source is unavailable.
   */
  get(clientId: number, brokerageId: string): Observable<Acknowledgements> {
    return forkJoin({
      documents: this.documentService
        .getDocumentsRequiringAcknowledgement(clientId, brokerageId)
        .pipe(catchError(() => of([]))),
      company: this.companyAcksApi
        .getOutstanding(clientId, brokerageId)
        .pipe(catchError(() => of([] as CompanyAcknowledgementResponse[]))),
    }).pipe(
      map(({ documents, company }) => {
        const documentAcknowledgements: Acknowledgements = documents.map((doc) =>
          this.convertDocumentToAcknowledgement(doc)
        );
        const companyAcknowledgements: Acknowledgements = company.map((ack) =>
          this.convertCompanyAckToAcknowledgement(ack, clientId)
        );
        return [...documentAcknowledgements, ...companyAcknowledgements];
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  private formatFileNameAsTitle(fileName: string): string {
    if (!fileName) return '';
    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
    const titleWithSpaces = nameWithoutExtension.replace(/([A-Z])/g, ' $1').trim();
    return titleWithSpaces;
  }

  private convertDocumentToAcknowledgement(doc: DocumentWithAcknowledgement): Acknowledgement {
    return {
      AcknowledgementType: 'Document',
      AcknowledgementId: doc.legacyDocumentId || 0,
      AcknowledgementTitle: this.formatFileNameAsTitle(doc.fileName),
      AcknowledgementFileName: doc.fileName,
      ContentType: doc.contentType,
      ClientId: doc.clientIds[0] || 0,
      DocumentId: doc.documentId && doc.documentId.length > 0 ? doc.documentId : undefined,
      Source: 'document',
    };
  }

  private convertCompanyAckToAcknowledgement(
    ack: CompanyAcknowledgementResponse,
    clientId: number
  ): Acknowledgement {
    return {
      AcknowledgementType: 'Document',
      AcknowledgementId: 0,
      AcknowledgementTitle: ack.type || this.formatFileNameAsTitle(ack.fileName),
      AcknowledgementFileName: ack.fileName,
      Version: ack.version,
      ContentType: ack.contentType,
      ClientId: clientId,
      CompanyAcknowledgementId: ack.companyAcknowledgementId,
      Source: 'company-acknowledgement',
    };
  }

  /**
   * Downloads an acknowledgement file. Routing order:
   *  1. Company-ack GUID present → Company Acknowledgements API
   *  2. Document UUID present → Documents API
   */
  download(
    documentId?: string,
    brokerageId?: string,
    companyAcknowledgementId?: string
  ): Observable<Blob> {
    if (companyAcknowledgementId && brokerageId) {
      return this.companyAcksApi.download(companyAcknowledgementId, brokerageId);
    }

    if (documentId && brokerageId) {
      return this.documentService.downloadDocumentByUuid(documentId, brokerageId);
    }

    return throwError(() => new Error('No document or company acknowledgement id to download'));
  }

  /**
   * Acknowledges a list of acknowledgements. Each item routes to the appropriate backend
   * based on its identifiers.
   */
  acknowledge(
    clientId: number,
    brokerageId: string,
    acknowledgements: {
      acknowledgementId: number;
      acknowledgementType: AcknowledgementType;
      documentId?: string;
      companyAcknowledgementId?: string;
    }[]
  ): Observable<AcknowledgeResponse[]> {
    const acknowledgementRequests: Observable<AcknowledgeResponse[]> = forkJoin(
      acknowledgements.map((ack) => {
        if (ack.companyAcknowledgementId) {
          return this.companyAcksApi
            .acknowledge(ack.companyAcknowledgementId, brokerageId)
            .pipe(
              map(
                (response): AcknowledgeResponse => ({
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  CompanyAcknowledgementId: ack.companyAcknowledgementId,
                  Acknowledged: response.acknowledged,
                  success: response.acknowledged,
                })
              ),
              catchError((error: HttpErrorResponse): Observable<AcknowledgeResponse> => {
                const result: AcknowledgeResponse = {
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  CompanyAcknowledgementId: ack.companyAcknowledgementId,
                  success: false,
                  error: error.message || 'Unknown error occurred',
                };
                return of(result);
              })
            );
        }

        if (ack.documentId) {
          return this.documentService
            .acknowledgeDocument(ack.documentId, clientId, brokerageId)
            .pipe(
              map(
                (response): AcknowledgeResponse => ({
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  Acknowledged: response.acknowledged,
                  success: response.acknowledged,
                })
              ),
              catchError((error: HttpErrorResponse): Observable<AcknowledgeResponse> => {
                const result: AcknowledgeResponse = {
                  AcknowledgementId: ack.acknowledgementId,
                  AcknowledgementType: ack.acknowledgementType,
                  success: false,
                  error: error.message || 'Unknown error occurred',
                };
                return of(result);
              })
            );
        }

        const result: AcknowledgeResponse = {
          AcknowledgementId: ack.acknowledgementId,
          AcknowledgementType: ack.acknowledgementType,
          success: false,
          error: 'No document or company acknowledgement id to acknowledge',
        };
        return of(result);
      })
    );

    return acknowledgementRequests.pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
}
